import { locationMapping } from "./constant";

const STATIC_RATES = {
  NZD: 0.58,
  AUD: 0.7,
  SGD: 0.78,
};

const getExchangeRate = (currency, conversionRates) => {
  if (conversionRates) {
    if (currency === "AUD") {
      return parseFloat(conversionRates.conversion_rate_aud);
    }
    if (currency === "SGD") {
      return parseFloat(conversionRates.conversion_rate_sgd);
    }
    return parseFloat(conversionRates.conversion_rate_nzd);
  }
  return STATIC_RATES[currency] || STATIC_RATES.NZD;
};

/**
 * Convert stored USD price to local currency using per-row conversion rates when available.
 * @param {number|string} price - USD price from database
 * @param {string} currency - NZD | AUD | SGD
 * @param {object} [conversionRates] - optional { conversion_rate_nzd, conversion_rate_aud, conversion_rate_sgd }
 */
export const convert_usd_to_nzd = (price, currency, conversionRates) => {
  const exchangeRate = getExchangeRate(currency || "NZD", conversionRates);
  if (!exchangeRate || Number.isNaN(exchangeRate)) {
    return "-";
  }
  if (typeof price !== "number") {
    return (parseFloat(price) / exchangeRate).toFixed(2);
  }
  return (price / exchangeRate).toFixed(2);
};

export const getNativeCurrencyForWebsite = (website) =>
  locationMapping[website]?.currency ?? "NZD";

const getRatesObject = (conversionRates) =>
  conversionRates?.conversion_rate_nzd != null ? conversionRates : undefined;

/**
 * USD-per-local rate for a currency from a price/promo row (or static fallback).
 * Same basis as convert_usd_to_nzd: USD = local × rate.
 */
export const getUsdPerLocalRate = (currency, conversionRates) => {
  const rates = getRatesObject(conversionRates);
  const rate = getExchangeRate(currency || "NZD", rates);
  if (!rate || Number.isNaN(rate) || rate <= 0) return null;
  return rate;
};

/**
 * Cross rate: 1 native (source) currency = x selected currency.
 * Rates are USD-per-local (USD = local × rate), so:
 * 1 AUD = (rate_aud / rate_nzd) NZD
 */
export const getCrossRateLabel = (
  nativeCurrency,
  displayCurrency,
  conversionRates
) => {
  if (!nativeCurrency || !displayCurrency || nativeCurrency === displayCurrency) {
    return null;
  }
  const rates = getRatesObject(conversionRates);
  const nativeRate = getExchangeRate(nativeCurrency, rates);
  const displayRate = getExchangeRate(displayCurrency, rates);
  if (
    !nativeRate ||
    !displayRate ||
    Number.isNaN(nativeRate) ||
    Number.isNaN(displayRate) ||
    displayRate === 0
  ) {
    return null;
  }
  const cross = nativeRate / displayRate;
  if (!Number.isFinite(cross) || cross <= 0) return null;
  return `1 ${nativeCurrency} = ${cross.toFixed(4)} ${displayCurrency}`;
};

/**
 * Convert USD to display currency and native shelf currency for a retailer.
 */
export const getPriceWithShelf = (
  priceUsd,
  displayCurrency,
  website,
  conversionRates
) => {
  const rates = getRatesObject(conversionRates);
  const display = convert_usd_to_nzd(priceUsd, displayCurrency, rates);
  const nativeCurrency = getNativeCurrencyForWebsite(website);
  const shelf = convert_usd_to_nzd(priceUsd, nativeCurrency, rates);
  const showShelf =
    nativeCurrency !== displayCurrency && display !== "-" && shelf !== "-";
  const fxLabel = showShelf
    ? getCrossRateLabel(nativeCurrency, displayCurrency, rates)
    : null;

  return { display, shelf, nativeCurrency, showShelf, fxLabel };
};

export const formatPriceRange = (prices) => {
  const valid = prices.filter((p) => p != null && !Number.isNaN(p));
  if (valid.length === 0) return "-";
  const unique = [...new Set(valid)].sort((a, b) => a - b);
  if (unique.length === 1) return unique[0].toFixed(2);
  return `${unique[0].toFixed(2)} - ${unique[unique.length - 1].toFixed(2)}`;
};
